import { useLocale as useLocaleR42 } from '@/lib/locale';
import { useLocale as useLocaleR40 } from '@/lib/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api, { setAuth, fmtPrice, currentSchoolCurrency } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { handleStripeDecline } from '@/lib/paymentDecline';
import { PaymentDeclineNotice } from '@/components/PaymentDeclineNotice';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { WaiverAgreement } from '@/components/WaiverAgreement';
import PasswordInput from '@/components/PasswordInput';
import DateOfBirthInput from '@/components/DateOfBirthInput';
import { Logo } from '@/components/Logo';
import Seo from '@/components/Seo';
import { loadStoredCart, clearStoredCart } from '@/components/classfinder/ClassFinderWizard';
import {
    Check, ArrowLeft, ArrowRight, ShieldCheck, Lock, Loader2,
    CheckCircle2, AlertTriangle, Repeat, ShoppingCart, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth.jsx';

/**
 * CartCheckout — Phase 103b+c
 * ---------------------------
 * The ONE-CHECKOUT page for the Class Finder cart. A family arrives with a
 * mixed cart (one-time classes + monthly memberships across multiple kids)
 * and pays ONCE:
 *
 *   1. Account (skipped when already logged in as a parent)
 *   2. Guardian info (+ legal & SMS consent)
 *   3. Swimmers (prefilled from the wizard, complete the details)
 *   4. Waiver
 *   5. Payment — POST /register (application leg) → POST cart/init →
 *      single Stripe Elements confirm → POST cart/complete (fan-out)
 *   6. Confirmation
 *
 * Memberships are PRORATED for the first month and recur on the coach's
 * configured billing day (owner rule — surfaced verbatim in the summary).
 */

const SMS_CONSENT_LABEL_PARENT =
    'I agree to receive automated text messages from Splash Pad for lesson updates, reminders, and coach communications.';
const buildSmsOptInDisclosure = (phone) =>
    `${SMS_CONSENT_LABEL_PARENT} By providing ${phone || 'the phone number above'}, you agree to receive recurring automated marketing and lesson-related text messages from Splash Pad. Consent is not a condition of any purchase. Msg frequency varies and can be daily during active lesson seasons. Msg and data rates may apply. Reply HELP for help or STOP to cancel.`;
const isValidPhone = (raw) => (String(raw || '').match(/\d/g) || []).length >= 10;

export default function CartCheckout() {
    const { t: t42 } = useLocaleR42();
    const { t: tr40 } = useLocaleR40();
    const { slug } = useParams();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const isParentPreview = params.get('parent_preview') === '1';

    const [cart, setCart] = useState(null);        // {items, swimmers}
    const [data, setData] = useState(null);        // public coach payload
    const [error, setError] = useState(null);
    const [legalVersion, setLegalVersion] = useState('');
    const [publishableKey, setPublishableKey] = useState('');

    const [step, setStep] = useState(0);
    const [account, setAccount] = useState({ email: '', password: '' });
    const [guardian, setGuardian] = useState({
        first_name: '', last_name: '', phone: '',
        emergency_contact_name: '', emergency_contact_phone: '',
        address_street: '', address_city: '', address_state: '', address_zip: '',
        address: '', how_did_you_hear: '',
    });
    const [students, setStudents] = useState([]);
    const [waiver, setWaiver] = useState({ signed_name: '', agreed: false, cancellation_agreed: false });
    const [agreedLegal, setAgreedLegal] = useState(false);
    const [guardianConsent, setGuardianConsent] = useState(false);
    const [smsOptIn, setSmsOptIn] = useState(false);

    // Payment machinery
    const [registering, setRegistering] = useState(false);
    const [registered, setRegistered] = useState(false);
    const [initData, setInitData] = useState(null);
    const [payError, setPayError] = useState('');
    const [completing, setCompleting] = useState(false);
    const [doneResult, setDoneResult] = useState(null);

    // ---- Bootstrapping ------------------------------------------------------
    useEffect(() => {
        const stored = loadStoredCart(slug);
        setCart(stored && stored.items?.length ? stored : { items: [], swimmers: [] });
        api.get(`/public/coach/${slug}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.response?.data?.detail || 'Coach not found'));
        api.get('/public/legal').then((r) => setLegalVersion(r.data?.version || '')).catch(() => {});
        api.get('/public/stripe-config').then((r) => setPublishableKey(r.data?.publishable_key || '')).catch(() => {});
    }, [slug]);

    // Prefill swimmers from the wizard cart (unique per swimmer).
    useEffect(() => {
        if (!cart?.swimmers) return;
        setStudents((prev) => {
            if (prev.length) return prev;
            const used = new Set((cart.items || []).map((i) => i.swimmer_uid));
            return (cart.swimmers || [])
                .filter((s) => used.has(s.uid))
                .map((s) => ({
                    uid: s.uid,
                    first_name: s.first_name || '',
                    last_name: '',
                    dob: s.dob || '',
                    gender: '',
                    swim_level: '',
                    medical_notes: '',
                    emergency_med_authorized: false,
                }));
        });
    }, [cart]);

    const coach = data?.coach;
    const settings = coach?.settings || {};
    const requireWaiver = settings.require_waiver !== false;
    const loggedInParent = !!user && user.role === 'parent';

    const STEPS = useMemo(() => {
        const s = [];
        if (!loggedInParent) s.push('Account');
        s.push('Guardian', 'Swimmers');
        if (requireWaiver) s.push('Waiver');
        s.push('Payment');
        return s;
    }, [loggedInParent, requireWaiver]);
    const stepName = STEPS[step] || 'Payment';

    const totals = useMemo(() => {
        let oneTime = 0; let monthly = 0;
        for (const it of (cart?.items || [])) {
            if (it.billing_type === 'one_time') oneTime += it.price_cents || 0;
            else if (it.billing_type === 'recurring_monthly') monthly += it.price_cents || 0;
        }
        const currency = (cart?.items || []).find((i) => i.currency)?.currency || currentSchoolCurrency();
        return { oneTime, monthly, currency };
    }, [cart]);

    // ---- Step validation ----------------------------------------------------
    const validateStep = () => {
        if (stepName === 'Account') {
            if (!account.email || !account.email.includes('@')) { toast.error(tr40("Enter a valid email.")); return false; }
            if (!account.password || account.password.length < 8) { toast.error(tr40("Password must be at least 8 characters.")); return false; }
            if (!agreedLegal) { toast.error(tr40("Please accept the Terms & Privacy Policy.")); return false; }
        }
        if (stepName === 'Guardian') {
            if (!guardian.first_name.trim() || !guardian.last_name.trim()) { toast.error(tr40("Enter your first and last name.")); return false; }
            if (!isValidPhone(guardian.phone)) { toast.error(tr40("Enter a valid mobile phone number (10+ digits).")); return false; }
        }
        if (stepName === 'Swimmers') {
            for (const s of students) {
                if (!s.first_name.trim() || !s.last_name.trim()) { toast.error(`Add a last name for ${s.first_name || 'each swimmer'}.`); return false; }
                if (!s.dob) { toast.error(`Add a birthdate for ${s.first_name}.`); return false; }
            }
            if (!guardianConsent) { toast.error(tr40("Please confirm you are the parent/legal guardian of the listed children.")); return false; }
        }
        if (stepName === 'Waiver') {
            if (!waiver.signed_name.trim()) { toast.error(tr40("Type your full name to sign the waiver.")); return false; }
            if (!waiver.agreed || !waiver.cancellation_agreed) { toast.error(tr40("Please agree to the Liability Waiver and Cancellation Policy.")); return false; }
        }
        return true;
    };

    const next = () => {
        if (!validateStep()) return;
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const back = () => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

    // ---- Register (application leg) + cart/init -----------------------------
    const startPayment = async () => {
        if (isParentPreview) { toast.info(tr40("Preview mode — payment is disabled here.")); return; }
        setRegistering(true);
        setPayError('');
        try {
            if (!registered) {
                const composedAddress = [
                    guardian.address_street,
                    [guardian.address_city, guardian.address_state, guardian.address_zip].filter(Boolean).join(' '),
                ].filter(Boolean).join(', ');
                const payload = {
                    guardian: { ...guardian, address: composedAddress || guardian.address },
                    students: students.map(({ uid, ...rest }) => rest),
                    guardian_consent_accepted: !!guardianConsent,
                };
                if (requireWaiver) {
                    payload.waiver = {
                        waiver_text: coach?.waiver_text || '',
                        signed_name: waiver.signed_name,
                        agreed: waiver.agreed,
                        cancellation_agreed: waiver.cancellation_agreed,
                        cancellation_general_text: coach?.cancellation_general_text || '',
                        cancellation_weather_text: coach?.cancellation_weather_text || '',
                        cancellation_sick_text: coach?.cancellation_sick_text || '',
                    };
                }
                if (account.email) payload.email = account.email;
                if (account.password) payload.password = account.password;
                if (legalVersion) payload.legal_accepted_version = legalVersion;
                if (smsOptIn) {
                    payload.sms_opt_in_accepted = true;
                    payload.sms_opt_in_phone = guardian.phone || '';
                    payload.sms_opt_in_text = buildSmsOptInDisclosure(guardian.phone);
                }
                const r = await api.post(`/public/coach/${slug}/register`, payload);
                if (r.data?.token) {
                    setAuth(r.data.token, r.data.user);
                    await refresh();
                }
                setRegistered(true);
            }
            // One PaymentIntent / SetupIntent for the whole cart.
            const initRes = await api.post(`/public/coach/${slug}/cart/init`, {
                items: (cart.items || []).map((it) => ({
                    package_id: it.package_id,
                    class_slot_ids: it.class_slot_ids || [],
                    swimmer_first_name: it.swimmer_first_name,
                    swimmer_dob: it.swimmer_dob || null,
                    single_lesson_at: it.single_lesson_at || null,
                })),
                parent_email: account.email || user?.email || '',
                parent_first_name: guardian.first_name,
                parent_last_name: guardian.last_name,
                parent_phone: guardian.phone || '',
            });
            setInitData(initRes.data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setPayError(typeof detail === 'string' ? detail : 'Could not start checkout. Please review your info and try again.');
        } finally {
            setRegistering(false);
        }
    };

    // Kick off registration + intent creation the moment Payment step opens.
    useEffect(() => {
        if (stepName === 'Payment' && !initData && !registering && !payError && cart?.items?.length) {
            startPayment();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepName]);

    const stripePromise = useMemo(() => {
        if (!publishableKey || !initData?.stripe_account_id) return null;
        return loadStripe(publishableKey, { stripeAccount: initData.stripe_account_id });
    }, [publishableKey, initData?.stripe_account_id]);

    const completeCart = async (confirmResult) => {
        setCompleting(true);
        try {
            const body = {
                items: (cart.items || []).map((it) => ({
                    package_id: it.package_id,
                    class_slot_ids: it.class_slot_ids || [],
                    swimmer_first_name: it.swimmer_first_name,
                    swimmer_dob: it.swimmer_dob || null,
                    single_lesson_at: it.single_lesson_at || null,
                })),
                intent_type: initData.intent_type,
                payment_method_id: confirmResult.payment_method_id || null,
            };
            if (initData.intent_type === 'payment_intent') body.payment_intent_id = confirmResult.intent_id;
            else body.setup_intent_id = confirmResult.intent_id;
            const r = await api.post(`/public/coach/${slug}/cart/complete`, body);
            setDoneResult(r.data);
            clearStoredCart(slug);
            try {
                const colors = ['#00d4ff', '#ff6b35', '#ffd700', '#1a3a5c'];
                confetti({ particleCount: 90, spread: 75, origin: { y: 0.6 }, colors });
            } catch { /* decorative */ }
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setPayError(typeof detail === 'string' ? detail : 'Payment went through but enrolling hit a snag — refresh your parent dashboard or contact your coach.');
        } finally {
            setCompleting(false);
        }
    };

    // ---- Render -------------------------------------------------------------
    if (error) {
        return (
            <div className="min-h-dvh bg-[#f4f7fb] flex flex-col items-center justify-center p-6 text-center">
                <Logo />
                <h1 className="mt-6 font-display text-2xl font-bold text-[#0a1628]">{tr40("Page unavailable")}</h1>
                <p className="mt-2 text-[#1a3a5c]">{error}</p>
                <Button asChild className="sd-cta mt-6"><Link to="/">{tr40("Back home")}</Link></Button>
            </div>
        );
    }
    if (!data || cart === null) {
        return <div className="p-6 max-w-3xl mx-auto space-y-3"><Skeleton className="h-12" /><Skeleton className="h-40" /><Skeleton className="h-40" /></div>;
    }
    if (!cart.items?.length && !doneResult) {
        return (
            <div className="min-h-dvh bg-[#f4f7fb] flex flex-col items-center justify-center p-6 text-center" data-testid="cart-checkout-empty">
                <ShoppingCart className="h-10 w-10 text-[#7a8aa0]" />
                <h1 className="mt-4 font-display text-2xl font-bold text-[#0a1628]">{tr40("Your cart is empty")}</h1>
                <p className="mt-2 text-[#1a3a5c]">{tr40("Head back to the class finder to pick your classes.")}</p>
                <Button asChild className="sd-cta mt-6" data-testid="cart-checkout-back-to-classes">
                    {/* Phase 104 — Class Finder lives in the private Parent Portal now */}
                    <Link to={isParentPreview ? `/coach/${slug}?parent_preview=1` : '/parent/coach'}>{tr40("Find classes")}</Link>
                </Button>
            </div>
        );
    }

    // Confirmation screen
    if (doneResult) {
        const enrollments = doneResult.enrollments || [];
        const failures = (doneResult.results || []).filter((r) => !r.ok);
        return (
            <div className="min-h-dvh bg-[#f4f7fb] py-10" data-testid="cart-checkout-confirmation">
                <div className="mx-auto max-w-2xl px-4 sm:px-6">
                    <Card className="sd-card p-6 sm:p-8 text-center">
                        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#e8fbf1]">
                            <CheckCircle2 className="h-8 w-8 text-[#16a34a]" />
                        </div>
                        <h1 className="mt-4 font-display text-2xl font-bold text-[#0a1628]">{tr40("You're all set! 🎉")}</h1>
                        <p className="mt-2 text-sm text-[#1a3a5c]"> {tr40("One checkout,")} {enrollments.length} {tr40("enrollment")}{enrollments.length === 1 ? '' : 's'} {tr40("with")} {coach?.display_name}.
                        </p>
                        <div className="mt-5 space-y-2 text-left">
                            {enrollments.map((e) => (
                                <div key={e.enrollment_id} className="rounded-xl border border-[#dde6f0] bg-white p-3.5" data-testid={`cart-confirm-item-${e.enrollment_id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="text-sm font-bold text-[#0a1628]">{e.package_name} — {e.swimmer}</div>
                                            <div className="mt-0.5 text-xs text-[#1a3a5c]/75">
                                                {e.class_slot_label || 'Schedule set with your coach'}
                                            </div>
                                            {e.billing_type === 'recurring_monthly' && (
                                                <div className="mt-1 text-xs text-[#0b7fa8]">
                                                    <Repeat className="mr-1 inline h-3 w-3" />
                                                    {fmtPrice(e.monthly_cents, e?.currency)}{tr40("/mo from")} {e.next_billing_date} {tr40("· first month prorated")}{e.prorated_cents > 0 ? ` (${fmtPrice(e.prorated_cents, e?.currency)} today)` : ''}
                                                </div>
                                            )}
                                        </div>
                                        <Badge className={e.status === 'active' ? 'bg-[#e8fbf1] text-[#16a34a] hover:bg-[#e8fbf1]' : 'bg-[#fff7df] text-[#92700c] hover:bg-[#fff7df]'}>
                                            {e.status === 'active' ? tr40("Enrolled") : tr40("Pending")}
                                        </Badge>
                                    </div>
                                </div>
                            ))}
                            {failures.map((f, i) => (
                                <div key={i} className="rounded-xl border border-[#ff6b35]/40 bg-[#fff5f0] p-3.5 text-xs text-[#b45309]" data-testid="cart-confirm-failure">
                                    <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> {f.error}
                                </div>
                            ))}
                        </div>
                        <Button asChild className="sd-cta sd-focus mt-6 h-12 w-full rounded-xl" data-testid="cart-confirm-dashboard-button">
                            <Link to="/parent">{tr40("Go to my parent dashboard")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
                        </Button>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-dvh bg-[#f4f7fb]" data-testid="cart-checkout-page">
            <Seo
                title={`Checkout with ${coach?.display_name || 'your coach'} | SplashPad`}
                description={`Complete your swim class enrollment with ${coach?.display_name || 'your coach'} — one secure checkout for every swimmer and class in your cart.`}
                canonical={`https://splashpad.pro/coach/${slug}`}
                noIndex
            />
            {isParentPreview && (
                <div className="flex items-center gap-2 border-b border-[#ffd700]/40 bg-[#fff8d6] px-4 py-2 text-xs text-[#0a1628]" data-testid="cart-checkout-preview-banner">
                    <Eye className="h-4 w-4" /> <strong>{tr40("Preview mode")}</strong> {tr40("— payment is disabled.")} </div>
            )}
            <header className="sticky top-0 z-30 sd-glass border-b">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
                    <div className="flex items-center gap-3">
                        {coach?.logo_url ? <img src={coach.logo_url} alt={coach.display_name} className="h-8 w-8 rounded-md object-cover" /> : <Logo size="sm" />}
                        <div className="text-sm text-[#1a3a5c]">{coach?.display_name} {tr40("· Checkout")}</div>
                    </div>
                    {/* Phase 104 — back link targets the Parent Portal's Find Classes tab
                        (the wizard no longer renders on the public page). Preview mode keeps
                        the public-page iframe URL so the coach overlay works unchanged. */}
                    <Link to={isParentPreview ? `/coach/${slug}?parent_preview=1` : '/parent/coach'} className="sd-focus inline-flex items-center gap-1 text-sm font-semibold text-[#1a3a5c] hover:text-[#0a1628]" data-testid="cart-checkout-back-link">
                        <ArrowLeft className="h-4 w-4" /> {tr40("Back to classes")} </Link>
                </div>
            </header>

            <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
                {/* Stepper */}
                <div className="flex flex-wrap items-center gap-2" data-testid="cart-checkout-stepper">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s}>
                            {i > 0 && <span className="text-[#c4d0de]">›</span>}
                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${i <= step ? 'text-[#0a1628]' : 'text-[#7a8aa0]'}`}>
                                <span className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold ${i < step ? 'bg-[#00d4ff] text-[#0a1628]' : i === step ? 'bg-[#0a1628] text-white' : 'bg-[#dde6f0] text-[#1a3a5c]'}`}>
                                    {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                                </span>
                                {s}
                            </span>
                        </React.Fragment>
                    ))}
                </div>

                <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_300px] lg:items-start">
                    <div>
                        {/* === Account === */}
                        {stepName === 'Account' && (
                            <Card className="sd-card p-5 sm:p-6" data-testid="cart-step-account">
                                <h2 className="font-display text-lg font-bold text-[#0a1628]">{tr40("Create your parent account")}</h2>
                                <p className="mt-1 text-sm text-[#1a3a5c]/75">{tr40("You'll use this to manage lessons, payments, and schedules.")}</p>
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <Label htmlFor="cart-email">{tr40("Email")}</Label>
                                        <Input id="cart-email" type="email" value={account.email} onChange={(e) => setAccount((a) => ({ ...a, email: e.target.value }))} className="mt-1 h-11 rounded-xl" placeholder={t42("you@example.com")} data-testid="cart-account-email-input" />
                                    </div>
                                    <div>
                                        <Label htmlFor="cart-password">{tr40("Password")}</Label>
                                        <PasswordInput id="cart-password" value={account.password} onChange={(e) => setAccount((a) => ({ ...a, password: e.target.value }))} className="mt-1 h-11 rounded-xl" placeholder={tr40("At least 8 characters")} data-testid="cart-account-password-input" />
                                    </div>
                                    <label className="flex items-start gap-3 text-sm text-[#1a3a5c]">
                                        <Checkbox checked={agreedLegal} onCheckedChange={(v) => setAgreedLegal(!!v)} className="mt-0.5" data-testid="cart-legal-checkbox" />
                                        <span> {tr40("I agree to SplashPad's")} <Link to="/terms" target="_blank" className="font-semibold underline">{tr40("Terms of Service")}</Link> {tr40("and")} <Link to="/privacy" target="_blank" className="font-semibold underline">{tr40("Privacy Policy")}</Link>
                                            {legalVersion && <span className="ml-1 text-[#1a3a5c]/60">{t42("(v")}{legalVersion})</span>}
                                        </span>
                                    </label>
                                    <p className="text-xs text-[#1a3a5c]/60">{tr40("Already have an account?")} <Link to={`/login?from=${encodeURIComponent(`/coach/${slug}/enroll-cart`)}`} className="font-semibold underline" data-testid="cart-login-link">{tr40("Log in")}</Link> {tr40("— your cart is saved.")}</p>
                                </div>
                            </Card>
                        )}

                        {/* === Guardian === */}
                        {stepName === 'Guardian' && (
                            <Card className="sd-card p-5 sm:p-6" data-testid="cart-step-guardian">
                                <h2 className="font-display text-lg font-bold text-[#0a1628]">{tr40("Parent / guardian info")}</h2>
                                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                    <div>
                                        <Label>{tr40("First name")}</Label>
                                        <Input value={guardian.first_name} onChange={(e) => setGuardian((g) => ({ ...g, first_name: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="cart-guardian-first-name" />
                                    </div>
                                    <div>
                                        <Label>{tr40("Last name")}</Label>
                                        <Input value={guardian.last_name} onChange={(e) => setGuardian((g) => ({ ...g, last_name: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="cart-guardian-last-name" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label>{tr40("Mobile phone")}</Label>
                                        <Input value={guardian.phone} onChange={(e) => setGuardian((g) => ({ ...g, phone: e.target.value }))} className="mt-1 h-11 rounded-xl" placeholder="(555) 123-4567" data-testid="cart-guardian-phone" />
                                        <label className="mt-2 flex items-start gap-3 text-xs text-[#1a3a5c]">
                                            <Checkbox checked={smsOptIn} onCheckedChange={(v) => setSmsOptIn(!!v)} className="mt-0.5" data-testid="cart-sms-optin-checkbox" />
                                            <span>{SMS_CONSENT_LABEL_PARENT} <span className="text-[#1a3a5c]/60">{tr40("(optional)")}</span></span>
                                        </label>
                                    </div>
                                    <div>
                                        <Label>{tr40("Emergency contact name")}</Label>
                                        <Input value={guardian.emergency_contact_name} onChange={(e) => setGuardian((g) => ({ ...g, emergency_contact_name: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="cart-guardian-emergency-name" />
                                    </div>
                                    <div>
                                        <Label>{tr40("Emergency contact phone")}</Label>
                                        <Input value={guardian.emergency_contact_phone} onChange={(e) => setGuardian((g) => ({ ...g, emergency_contact_phone: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="cart-guardian-emergency-phone" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label>{tr40("Street address")}</Label>
                                        <Input value={guardian.address_street} onChange={(e) => setGuardian((g) => ({ ...g, address_street: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="cart-guardian-street" />
                                    </div>
                                    <div>
                                        <Label>{tr40("City")}</Label>
                                        <Input value={guardian.address_city} onChange={(e) => setGuardian((g) => ({ ...g, address_city: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="cart-guardian-city" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>{tr40("State")}</Label>
                                            <Input value={guardian.address_state} onChange={(e) => setGuardian((g) => ({ ...g, address_state: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="cart-guardian-state" />
                                        </div>
                                        <div>
                                            <Label>{tr40("ZIP")}</Label>
                                            <Input value={guardian.address_zip} onChange={(e) => setGuardian((g) => ({ ...g, address_zip: e.target.value }))} className="mt-1 h-11 rounded-xl" data-testid="cart-guardian-zip" />
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* === Swimmers === */}
                        {stepName === 'Swimmers' && (
                            <Card className="sd-card p-5 sm:p-6" data-testid="cart-step-swimmers">
                                <h2 className="font-display text-lg font-bold text-[#0a1628]">{tr40("Your swimmers")}</h2>
                                <p className="mt-1 text-sm text-[#1a3a5c]/75">{tr40("Complete the details for each swimmer in your cart.")}</p>
                                <div className="mt-4 space-y-4">
                                    {students.map((s, idx) => (
                                        <div key={s.uid} className="rounded-2xl border border-[#dde6f0] bg-white p-4" data-testid={`cart-swimmer-card-${idx}`}>
                                            <div className="grid gap-3 sm:grid-cols-2">
                                                <div>
                                                    <Label>{tr40("First name")}</Label>
                                                    <Input value={s.first_name} onChange={(e) => setStudents((arr) => arr.map((x, i) => i === idx ? { ...x, first_name: e.target.value } : x))} className="mt-1 h-11 rounded-xl" data-testid={`cart-swimmer-first-${idx}`} />
                                                </div>
                                                <div>
                                                    <Label>{tr40("Last name")}</Label>
                                                    <Input value={s.last_name} onChange={(e) => setStudents((arr) => arr.map((x, i) => i === idx ? { ...x, last_name: e.target.value } : x))} className="mt-1 h-11 rounded-xl" data-testid={`cart-swimmer-last-${idx}`} />
                                                </div>
                                                <div>
                                                    <Label>{tr40("Birthdate")}</Label>
                                                    <div className="mt-1">
                                                        <DateOfBirthInput value={s.dob} onChange={(iso) => setStudents((arr) => arr.map((x, i) => i === idx ? { ...x, dob: iso } : x))} data-testid={`cart-swimmer-dob-${idx}`} />
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label>{tr40("Medical notes")} <span className="text-[#1a3a5c]/50">{tr40("(optional)")}</span></Label>
                                                    <Input value={s.medical_notes} onChange={(e) => setStudents((arr) => arr.map((x, i) => i === idx ? { ...x, medical_notes: e.target.value } : x))} className="mt-1 h-11 rounded-xl" placeholder={tr40("Allergies, conditions…")} data-testid={`cart-swimmer-medical-${idx}`} />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <label className="flex items-start gap-3 text-sm text-[#1a3a5c]">
                                        <Checkbox checked={guardianConsent} onCheckedChange={(v) => setGuardianConsent(!!v)} className="mt-0.5" data-testid="cart-guardian-consent-checkbox" />
                                        <span>{tr40("I confirm I am the parent/legal guardian of the child(ren) listed and consent to SplashPad processing their information for swim lesson delivery.")}</span>
                                    </label>
                                </div>
                            </Card>
                        )}

                        {/* === Waiver === */}
                        {stepName === 'Waiver' && (
                            <Card className="sd-card p-5 sm:p-6" data-testid="cart-step-waiver">
                                <h2 className="font-display text-lg font-bold text-[#0a1628]">{tr40("Liability waiver & policies")}</h2>
                                <p className="mt-1 text-sm text-[#1a3a5c]/80">{tr40("Tap a document to read it, agree, then type your name to sign.")}</p>
                                {/* Owner decision (2026-09-11): documents open on tap; one checkbox covers both. */}
                                <div className="mt-4">
                                    <WaiverAgreement
                                        coachName={coach?.display_name}
                                        waiverText={coach?.waiver_text}
                                        cancellation={{ general: coach?.cancellation_general_text, weather: coach?.cancellation_weather_text, sick: coach?.cancellation_sick_text }}
                                        agreed={waiver.agreed && waiver.cancellation_agreed}
                                        onAgreedChange={(v) => setWaiver((w) => ({ ...w, agreed: v, cancellation_agreed: v }))}
                                        testIdPrefix="cart"
                                    />
                                </div>
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <Label>{tr40("Type your full name to sign")}</Label>
                                        <Input value={waiver.signed_name} onChange={(e) => setWaiver((w) => ({ ...w, signed_name: e.target.value }))} className="mt-1 h-11 max-w-sm rounded-xl" placeholder={tr40("Your full legal name")} data-testid="cart-waiver-signature-input" />
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* === Payment === */}
                        {stepName === 'Payment' && (
                            <div className="space-y-4" data-testid="cart-step-payment">
                                {registering && (
                                    <Card className="sd-card flex items-center gap-2 p-6 text-sm text-[#1a3a5c]/70" data-testid="cart-payment-loading">
                                        <Loader2 className="h-4 w-4 animate-spin" /> {tr40("Creating your account & setting up one checkout…")} </Card>
                                )}
                                {payError && (
                                    <Card className="sd-card border-[#ff6b35]/40 p-5" data-testid="cart-payment-error">
                                        <div className="flex items-start gap-3">
                                            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#ff6b35]" />
                                            <div>
                                                <div className="font-semibold text-[#0a1628]">{tr40("Checkout hit a snag")}</div>
                                                <p className="mt-1 text-sm text-[#1a3a5c]">{payError}</p>
                                                <Button variant="outline" className="mt-3 h-10 rounded-xl" onClick={() => { setPayError(''); startPayment(); }} data-testid="cart-payment-retry-button"> {tr40("Try again")} </Button>
                                            </div>
                                        </div>
                                    </Card>
                                )}
                                {initData && stripePromise && (
                                    <Elements
                                        stripe={stripePromise}
                                        options={{
                                            clientSecret: initData.client_secret,
                                            appearance: {
                                                theme: 'stripe',
                                                variables: {
                                                    colorPrimary: '#00d4ff', colorBackground: '#ffffff',
                                                    colorText: '#0a1628', colorDanger: '#ff6b35',
                                                    fontFamily: 'DM Sans, system-ui, sans-serif', borderRadius: '12px',
                                                },
                                            },
                                        }}
                                    >
                                        <CartPayInner
                                            initData={initData}
                                            coachDisplayName={coach?.display_name}
                                            completing={completing}
                                            onConfirmed={completeCart}
                                            coachSlug={slug}
                                            parentEmail={account.email || user?.email || ''}
                                        />
                                    </Elements>
                                )}
                            </div>
                        )}

                        {/* Nav buttons */}
                        {stepName !== 'Payment' && (
                            <div className="mt-5 flex items-center justify-between">
                                <Button variant="outline" onClick={back} disabled={step === 0} className="h-11 rounded-xl" data-testid="cart-back-button">
                                    <ArrowLeft className="mr-1 h-4 w-4" /> {tr40("Back")} </Button>
                                <Button onClick={next} className="sd-cta sd-focus h-11 rounded-xl px-6" data-testid="cart-next-button"> {tr40("Continue")} <ArrowRight className="ml-1 h-4 w-4" />
                                </Button>
                            </div>
                        )}
                        {stepName === 'Payment' && (
                            <div className="mt-5">
                                <Button variant="outline" onClick={back} className="h-11 rounded-xl" data-testid="cart-back-button">
                                    <ArrowLeft className="mr-1 h-4 w-4" /> {tr40("Back")} </Button>
                            </div>
                        )}
                    </div>

                    {/* Order summary */}
                    <Card className="sd-card p-5 lg:sticky lg:top-20" data-testid="cart-checkout-summary">
                        <h3 className="font-display text-sm font-bold text-[#0a1628]">{tr40("Order summary")}</h3>
                        <div className="mt-3 space-y-2">
                            {(cart.items || []).map((it) => (
                                <div key={it.uid} className="flex items-start justify-between gap-2 text-sm">
                                    <div className="min-w-0">
                                        <div className="truncate font-semibold text-[#0a1628]">{it.package_name}</div>
                                        <div className="text-xs text-[#1a3a5c]/70">{it.swimmer_first_name}{it.slot_labels ? ` · ${it.slot_labels}` : ''}</div>
                                    </div>
                                    <div className="shrink-0 font-semibold text-[#0a1628]">
                                        {fmtPrice(it.price_cents, it?.currency)}{it.billing_type === 'recurring_monthly' ? tr40("/mo") : ''}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 space-y-1 border-t border-[#dde6f0] pt-3 text-sm">
                            {initData ? (
                                <>
                                    <div className="flex justify-between font-bold text-[#0a1628]" data-testid="cart-summary-due-today">
                                        <span>{tr40("Due today")}</span><span>{fmtPrice(initData.due_today_cents, initData?.currency)}</span>
                                    </div>
                                    {initData.prorated_today_cents > 0 && (
                                        <div className="flex justify-between text-xs text-[#1a3a5c]/70">
                                            <span>{tr40("Includes prorated first month")}</span><span>{fmtPrice(initData.prorated_today_cents, initData?.currency)}</span>
                                        </div>
                                    )}
                                    {initData.monthly_total_cents > 0 && (
                                        <div className="flex justify-between text-xs text-[#1a3a5c]/70" data-testid="cart-summary-monthly">
                                            <span>{tr40("Then monthly")}</span><span>{fmtPrice(initData.monthly_total_cents, initData?.currency)}{tr40("/mo")}</span>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {totals.oneTime > 0 && (
                                        <div className="flex justify-between text-[#1a3a5c]"><span>{tr40("One-time classes")}</span><span>{fmtPrice(totals.oneTime, totals.currency)}</span></div>
                                    )}
                                    {totals.monthly > 0 && (
                                        <div className="flex justify-between text-[#1a3a5c]"><span>{tr40("Memberships")}</span><span>{fmtPrice(totals.monthly, totals.currency)}{tr40("/mo")}</span></div>
                                    )}
                                    {totals.monthly > 0 && (
                                        <p className="pt-1 text-[11px] text-[#1a3a5c]/60">{tr40("First month prorated — exact total shown at payment.")}</p>
                                    )}
                                </>
                            )}
                        </div>
                        <p className="mt-3 flex items-center gap-1 text-[11px] text-[#1a3a5c]/60">
                            <ShieldCheck className="h-3.5 w-3.5" /> {tr40("Secured by Stripe · one card entry for everything")} </p>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function CartPayInner({ initData, coachDisplayName, completing, onConfirmed, coachSlug, parentEmail }) {
    const { t: tr40 } = useLocaleR40();
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errMsg, setErrMsg] = useState('');
    const [decline, setDecline] = useState(null);
    const tryAnotherCard = () => {
        setDecline(null); setErrMsg('');
        try { elements?.getElement('payment')?.clear(); } catch { /* noop */ }
        try { elements?.getElement('payment')?.focus(); } catch { /* noop */ }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setErrMsg('');
        setDecline(null);
        const isSetup = initData.intent_type === 'setup_intent';
        const confirmFn = isSetup ? stripe.confirmSetup : stripe.confirmPayment;
        const result = await confirmFn({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: 'if_required',
        });
        if (result.error) {
            setDecline(handleStripeDecline(result.error, {
                context: 'cart',
                coachSlug,
                amountCents: initData?.due_today_cents ?? initData?.total_cents ?? initData?.amount_cents,
                packageName: initData?.summary_label || 'Cart checkout',
                parentEmail,
                paymentIntentId: initData?.intent_id || initData?.payment_intent_id,
            }));
            setSubmitting(false);
            return;
        }
        const intent = result.paymentIntent || result.setupIntent;
        await onConfirmed({
            intent_id: intent?.id,
            payment_method_id: typeof intent?.payment_method === 'string' ? intent.payment_method : intent?.payment_method?.id,
        });
        setSubmitting(false);
    };

    const busy = submitting || completing;
    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="cart-pay-form">
            <Card className="sd-card p-4" data-testid="cart-pay-summary">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div>
                        <div className="font-semibold text-[#0a1628]">{tr40("One checkout — everything in your cart")}</div>
                        <div className="text-xs text-[#1a3a5c]/70">
                            {initData.monthly_total_cents > 0
                                ? `Memberships prorate today, then ${fmtPrice(initData.monthly_total_cents, initData?.currency)}/mo on your coach's billing day.`
                                : tr40("One-time charge today.")}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs uppercase tracking-wide text-[#1a3a5c]/60">{tr40("Due today")}</div>
                        <div className="font-display text-2xl font-bold text-[#0a1628]" data-testid="cart-pay-due-today">{fmtPrice(initData.due_today_cents, initData?.currency)}</div>
                    </div>
                </div>
            </Card>
            <Card className="sd-card p-4">
                <PaymentElement options={{ layout: 'tabs' }} />
            </Card>
            <div className="flex items-start gap-3 rounded-xl border border-[#00d4ff]/25 bg-[#e6f9fd] p-4 text-sm text-[#0a1628]" data-testid="cart-pay-card-on-file-notice">
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-[#00d4ff]" aria-hidden="true" />
                <span className="leading-relaxed">
                    <strong>{tr40("Your card will be saved on file.")}</strong>{' '}
                    <span className="text-[#1a3a5c]/85"> {tr40("By paying, you authorize")} {coachDisplayName || 'your coach'} {tr40("to save this card and charge it for the items in your cart — including prorated first-month and recurring monthly membership charges — and future lessons or packages. Manage saved cards anytime from your parent portal.")} </span>
                </span>
            </div>
            {decline && (
                <PaymentDeclineNotice decline={decline} idPrefix="cart-decline" onTryAnother={tryAnotherCard} onRetry={() => setDecline(null)} />
            )}
            {errMsg && !decline && (
                <div className="rounded-xl border border-[#ff6b35]/40 bg-[#fff5f0] p-3 text-sm text-[#ff6b35]" data-testid="cart-pay-error-msg">{errMsg}</div>
            )}
            <Button type="submit" disabled={!stripe || busy} className="sd-cta sd-focus h-12 w-full rounded-xl text-base" data-testid="cart-pay-submit-button">
                {busy
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {completing ? tr40("Enrolling your swimmers…") : tr40("Processing…")}</>
                    : <><CheckCircle2 className="mr-2 h-4 w-4" /> {initData.due_today_cents > 0 ? `Pay ${fmtPrice(initData.due_today_cents, initData?.currency)} once` : tr40("Save card & enroll")}</>}
            </Button>
            <p className="text-center text-xs text-[#1a3a5c]/60">
                <Lock className="mr-1 inline h-3 w-3" /> {tr40("Secured by Stripe. SplashPad takes a small platform fee — your coach receives the rest on their next payout.")} </p>
        </form>
    );
}
